// Companion/blocks/studySetSession.ts
//
// "Is the student in the middle of something?" — the one question the launch
// flow has to answer before it takes them somewhere else (ORA-681).
//
// Pure, because it is the whole reason a confirm dialog appears: a false
// positive nags on every launch, and a false negative throws away a half-taken
// block. The caller reads the signals out of the store and the router; this
// only decides.

import type { StudyMode } from '@/slices/studyContext/studyContextSlice';

/**
 * The study routes that run something. The summary screen is deliberately not
 * one of them: it is the end of a session, not a session, and stopping to
 * confirm on the way out of it would be noise.
 */
export const STUDY_SESSION_PATHS = [
  '/study/qbank/block',
  '/study/qbank/block/review',
  '/study/flashcards',
  '/study/video',
] as const;

const BLOCK_PATHS: readonly string[] = [
  '/study/qbank/block',
  '/study/qbank/block/review',
];

/** The study route the student is on, or null when they are anywhere else. */
export const studySessionPathOf = (pathname: string): string | null =>
  STUDY_SESSION_PATHS.find((path) => path === pathname) ?? null;

export interface MidStudySessionSignals {
  pathname: string;
  studyMode: StudyMode;
  /** A QBank block is loaded and has not been submitted. */
  hasUnfinishedBlock: boolean;
  /** A flashcard run is ready with a card still to rate. */
  hasRunningFlashcards: boolean;
}

/**
 * Whether launching a set now would abandon something the student has open.
 *
 * Standalone study always counts: the mode exists precisely because a block or
 * a run was opened outside the daily session and has somewhere of its own to
 * return to. Otherwise it takes being on a study route with something actually
 * running — the routes are reachable with nothing loaded, and an empty screen
 * is not a session worth warning about.
 */
export const isMidStudySession = ({
  pathname,
  studyMode,
  hasUnfinishedBlock,
  hasRunningFlashcards,
}: MidStudySessionSignals): boolean => {
  if (studyMode === 'standalone') return true;

  const path = studySessionPathOf(pathname);
  if (!path) return false;

  if (BLOCK_PATHS.includes(path)) return hasUnfinishedBlock;
  if (path === '/study/flashcards') return hasRunningFlashcards;

  // The video runner has no "unfinished" of its own — a part-watched video is
  // saved continuously — so being on it is the signal.
  return true;
};
